import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  currentProject,
  selectFirstBuilding,
  selectProjectHasOnlyOneBuilding,
  selectTotalBuildingAndFloorCount,
} from 'spw-redux/modules/projects/selectors';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ButtonBase,
  Checkbox,
  Menu,
  MenuItem,
} from '@mui/material';
import { IBuilding, IFloor } from 'spw-redux/modules/projects/types';
import { Column } from 'spw-shared/globalComponents';
import { DefaultChevron } from 'spw-shared/globalComponents/base-select';
import styled from 'styled-components';
import { colors } from 'spw-theme';
import { ProgramSummaryParams } from 'spw-redux/modules/program-summary/types';
import { setSelectedLevels } from 'spw-redux/modules/program-summary/actions';
import { selectSelectedLevelsExpanded } from 'spw-redux/modules/program-summary/esa/selectors';

const SelectButton = styled(ButtonBase)`
  && {
    border-radius: 0;
    width: 296px;
    height: 52px;
    border: 1px solid #a8a8a8;
    display: flex;
    justify-content: space-between;
    text-align: start;
    padding: 8px 16px;
  }
`;

const BuildingAccordion = styled(Accordion)`
  && {
    margin: 0;
    padding: 0;
  }

  &&:before {
    height: 0px;
  }

  &.Mui-expanded {
    margin: 0 !important;
  }

  .MuiAccordionSummary-content,
  .MuiAccordionSummary-content.Mui-expanded {
    margin: 8px 0;
  }
`;

const BuildingAccordionTitle = styled(AccordionSummary)`
  && {
    padding: 0 16px;
  }
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const CompactMenuItem = styled(MenuItem)`
  && {
    padding: 0 8px;
  }

  & > .MuiCheckbox-root {
    padding: 6px;
  }
`;

const GrayCheckBox = styled(Checkbox)`
  && {
    color: ${colors.fontColor} !important;
  }
`;

const PrimaryLabel = styled.span`
  && {
    font-size: 14px;
  }
`;

const SecondaryLabel = styled.span`
  && {
    font-size: 12px;
    line-height: 16px;
    color: ${colors.mainGray};
  }
`;

const MenuWrapper = styled.div<{ $top?: number }>`
  ${({ $top }) => {
    if ($top) {
      return `
        & .MuiMenu-paper {
          top: ${$top}px !important;
        }
      `;
    }
    return '';
  }}
`;

type BuildingMenuItemProps = {
  building: IBuilding;
  selectedFloorIds: IFloor['id'][];
  selectedBuildingIds: IBuilding['id'][];
  handleBuildingClick: (building: IBuilding) => void;
  handleFloorClick: (selectedFloorId: number, floorBuilding: IBuilding) => void;
};

const BuildingMenuItem = ({
  building,
  selectedFloorIds,
  selectedBuildingIds,
  handleBuildingClick,
  handleFloorClick,
}: BuildingMenuItemProps) => {
  const numberOfFloorsSelected = selectedBuildingIds.includes(building.id)
    ? building.floors.length // all floors if building is selected
    : building.floors.filter((f) => selectedFloorIds.includes(f.id)).length;

  return (
    <BuildingAccordion key={building.id} elevation={0}>
      <BuildingAccordionTitle expandIcon={<DefaultChevron />}>
        <Column>
          <PrimaryLabel>{building.streetAddress}</PrimaryLabel>
          <SecondaryLabel>
            {numberOfFloorsSelected} of {building.floors.length} floors selected
          </SecondaryLabel>
        </Column>
      </BuildingAccordionTitle>
      <AccordionDetails style={{ flexDirection: 'column', padding: 0 }}>
        <CompactMenuItem
          key={`building-option-${building.id}`}
          value={`BUILDING-${building.id}`}
          onClick={() => handleBuildingClick(building)}
        >
          <GrayCheckBox checked={selectedBuildingIds.includes(building.id)} />
          <PrimaryLabel>All floors</PrimaryLabel>
        </CompactMenuItem>
        {building.floors.map((floor) => (
          <CompactMenuItem
            key={`${floor.name}-${floor.id}`}
            value={floor.id}
            onClick={() => handleFloorClick(floor.id, building)}
          >
            <GrayCheckBox
              checked={
                selectedFloorIds.includes(floor.id) ||
                selectedBuildingIds.includes(building.id)
              }
            />
            <PrimaryLabel>{floor.name}</PrimaryLabel>
          </CompactMenuItem>
        ))}
      </AccordionDetails>
    </BuildingAccordion>
  );
};

type ProgramSummarySelectMenuProps = {
  initialSelection: ProgramSummaryParams;
};

export const ProgramSummarySelectMenu = ({
  initialSelection,
}: ProgramSummarySelectMenuProps) => {
  const dispatch = useDispatch();
  const project = useSelector(currentProject);
  const projectHasOnlyOneBuilding = useSelector(
    selectProjectHasOnlyOneBuilding
  );
  const firstBuilding = useSelector(selectFirstBuilding);
  const [totalBuildingCount, totalFloorCount] = useSelector(
    selectTotalBuildingAndFloorCount
  );
  const { allSelectedFloorIds, allSelectedBuildingIds } = useSelector(
    selectSelectedLevelsExpanded
  );
  const [selectedFloorIds, setSelectedFloorIds] = useState<
    ProgramSummaryParams['floorIds']
  >([]);
  const [selectedBuildingIds, setSelectedBuildingIds] = useState<
    ProgramSummaryParams['buildingIds']
  >([]);
  const allBuildingsAreSelected = useMemo(
    () => selectedBuildingIds.length === totalBuildingCount,
    [selectedBuildingIds, totalBuildingCount]
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [top, setTop] = useState<number>();
  const [open, setOpen] = useState(false);

  const openMenu = (event: SyntheticEvent) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
    setTop(event.currentTarget.getBoundingClientRect().bottom);
  };

  const closeMenu = useCallback(() => {
    setOpen(false);
    if (selectedFloorIds.length === 0 && selectedBuildingIds.length === 0) {
      setSelectedFloorIds(initialSelection.floorIds);
      setSelectedBuildingIds(initialSelection.buildingIds);
    }
  }, [
    initialSelection.buildingIds,
    initialSelection.floorIds,
    selectedBuildingIds.length,
    selectedFloorIds.length,
  ]);

  const handleScroll = useCallback(() => {
    if (anchorEl) {
      const anchorBottom = anchorEl.getBoundingClientRect().bottom;
      setTop(anchorBottom);
    }
  }, [anchorEl]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    setSelectedFloorIds(initialSelection.floorIds);
    setSelectedBuildingIds(initialSelection.buildingIds);
  }, [initialSelection]);

  const deselectFloor = (floorId: IFloor['id']) =>
    setSelectedFloorIds((prev) => prev.filter((id) => id !== floorId));

  const selectFloor = (floorId: IFloor['id']) =>
    setSelectedFloorIds((prev) => [...prev, floorId]);

  const deselectBuilding = (buildingId: IBuilding['id']) =>
    setSelectedBuildingIds((prev) => prev.filter((id) => id !== buildingId));

  const selectBuilding = (buildingId: IBuilding['id']) =>
    setSelectedBuildingIds((prev) => [...prev, buildingId]);

  const handleAllFloorsClick = useCallback(() => {
    if (allBuildingsAreSelected) {
      setSelectedBuildingIds([]);
      setSelectedFloorIds([]);
    } else {
      setSelectedFloorIds([]);
      setSelectedBuildingIds(project?.buildings?.map((b) => b.id));
    }
  }, [project?.buildings, allBuildingsAreSelected]);

  const handleFloorClick = useCallback(
    (selectedFloorId: number, floorBuilding: IBuilding) => {
      const buildingFloorIds = floorBuilding.floors.map((f: IFloor) => f.id);
      if (selectedBuildingIds.includes(floorBuilding.id)) {
        // if the building is already selected
        // deselect it
        deselectBuilding(floorBuilding.id);
        // select all the buildings floors except the selected floor
        const floorsToSelect = buildingFloorIds.filter(
          (id: IFloor['id']) => id !== selectedFloorId
        );
        setSelectedFloorIds((prev) => [...prev, ...floorsToSelect]);
      } else if (
        buildingFloorIds.every(
          (id) => selectedFloorIds.includes(id) || id === selectedFloorId
        )
      ) {
        // if all floors of a building get selected
        // select the building
        selectBuilding(floorBuilding.id);
        // deselect all the floors for that building
        setSelectedFloorIds((prev) =>
          prev.filter((id) => !buildingFloorIds.includes(id))
        );
      } else if (selectedFloorIds.includes(selectedFloorId)) {
        deselectFloor(selectedFloorId);
      } else {
        selectFloor(selectedFloorId);
      }
    },
    [selectedFloorIds, selectedBuildingIds]
  );

  const handleBuildingClick = useCallback(
    (selectedBuilding: IBuilding) => {
      const selectedBuildingFloorIds = selectedBuilding.floors.map(
        (f: IFloor) => f.id
      );
      // deselect floors for that building
      setSelectedFloorIds((prev) =>
        prev.filter((id) => !selectedBuildingFloorIds.includes(id))
      );
      if (selectedBuildingIds.includes(selectedBuilding.id)) {
        deselectBuilding(selectedBuilding.id);
      } else {
        selectBuilding(selectedBuilding.id);
      }
    },
    [selectedBuildingIds]
  );

  const selectMenuSummary = useMemo(() => {
    const totalSelectedBuildingsCount = allSelectedBuildingIds.length;
    const totalSelectedFloorsCount = allSelectedFloorIds.length;
    return projectHasOnlyOneBuilding ? (
      <Column>
        <PrimaryLabel>{firstBuilding.streetAddress}</PrimaryLabel>
        <SecondaryLabel>
          {totalSelectedFloorsCount === 1
            ? firstBuilding.floors.find((f) => f.id === allSelectedFloorIds[0])
                .name || ''
            : `${totalSelectedFloorsCount} of ${firstBuilding.floors.length} Floors`}
        </SecondaryLabel>
      </Column>
    ) : (
      <PrimaryLabel>
        {allBuildingsAreSelected && 'All ('}
        {totalSelectedBuildingsCount} building
        {totalSelectedBuildingsCount === 1 ? '' : 's'}
        {', '}
        {totalSelectedFloorsCount} floor
        {totalSelectedFloorsCount === 1 ? '' : 's'}
        {allBuildingsAreSelected && ')'}
      </PrimaryLabel>
    );
  }, [
    firstBuilding,
    projectHasOnlyOneBuilding,
    allBuildingsAreSelected,
    allSelectedBuildingIds,
    allSelectedFloorIds,
  ]);

  useEffect(() => {
    if (selectedFloorIds.length > 0 || selectedBuildingIds.length > 0) {
      dispatch(
        setSelectedLevels({
          floorIds: selectedFloorIds,
          buildingIds: selectedBuildingIds,
        })
      );
    }
  }, [dispatch, selectedFloorIds, selectedBuildingIds]);

  return (
    <>
      <SelectButton onClick={openMenu}>
        {selectMenuSummary}
        <DefaultChevron />
      </SelectButton>

      <MenuWrapper $top={top}>
        {projectHasOnlyOneBuilding ? (
          <Menu
            anchorEl={anchorEl}
            disableScrollLock
            disablePortal
            keepMounted
            open={open}
            onClose={closeMenu}
            MenuListProps={{ style: { width: '296px' } }}
            anchorOrigin={{
              horizontal: 'left',
              vertical: 'bottom',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <CompactMenuItem onClick={() => handleAllFloorsClick()}>
              <GrayCheckBox checked={allBuildingsAreSelected} />
              <PrimaryLabel>All floors</PrimaryLabel>
            </CompactMenuItem>
            {firstBuilding.floors.map((floor) => (
              <CompactMenuItem
                key={`${floor.name}-${floor.id}`}
                value={floor.id}
                onClick={() => handleFloorClick(floor.id, firstBuilding)}
              >
                <GrayCheckBox
                  checked={
                    selectedFloorIds.includes(floor.id) ||
                    selectedBuildingIds.includes(firstBuilding.id)
                  }
                />
                <PrimaryLabel>{floor.name}</PrimaryLabel>
              </CompactMenuItem>
            ))}
          </Menu>
        ) : (
          <Menu
            anchorEl={anchorEl}
            disableScrollLock
            disablePortal
            keepMounted
            open={open}
            onClose={closeMenu}
            slotProps={{
              paper: {
                style: { width: '296px' },
              },
            }}
            MenuListProps={{
              style: {
                width: '100%',
                maxHeight: 'calc(100vh - 320px)',
                scrollbarGutter: 'stable',
              },
            }}
            anchorOrigin={{
              horizontal: 'left',
              vertical: 'bottom',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <CompactMenuItem onClick={() => handleAllFloorsClick()}>
              <GrayCheckBox checked={allBuildingsAreSelected} />
              <PrimaryLabel>
                All ({totalBuildingCount} building
                {totalBuildingCount === 1 ? '' : 's'}
                {', '}
                {totalFloorCount} floor
                {totalFloorCount === 1 ? '' : 's'})
              </PrimaryLabel>
            </CompactMenuItem>
            {project?.buildings?.map((b) => (
              <BuildingMenuItem
                key={b.id}
                building={b}
                selectedFloorIds={selectedFloorIds}
                selectedBuildingIds={selectedBuildingIds}
                handleBuildingClick={handleBuildingClick}
                handleFloorClick={handleFloorClick}
              />
            ))}
          </Menu>
        )}
      </MenuWrapper>
    </>
  );
};
